/**
 * useApiService
 *
 * Enhanced API hook with optional, in-memory caching and cache-control utilities.
 *
 * What you get:
 * - cache.enabled: Turn caching on/off per-hook.
 * - cache.strategy:
 *   - "cache-first": If a valid cache entry exists, return it and skip the network.
 *   - "stale-while-revalidate": Return cached data immediately, then refresh in the background.
 *   - "network-only": Always fetch from the backend (bypasses cache reads).
 * - cache.ttlMs: Time-to-live (ms) for a cache entry. Defaults to 1 hour when caching is enabled; expired entries are treated as a miss and will be refetched on the next call.
 * - cache.key: Optional custom cache key. Use this to group requests under a shared key (e.g., ignore payload/params).
 * - In-flight de-duplication: Parallel calls with the same key share a single network request.
 * - Helpers: revalidate() to force-refresh and update the cache; invalidate() to remove an entry.
 *
 * Cache scope and persistence:
 * - The cache is an in-memory Map that lives at the module level. It persists for as long as this module instance
 *   stays loaded (usually per-tab). It is cleared on full reloads/Hot Module Replacement.
 *
 * Cache key design:
 * - By default the key includes: method + apiName + url + params + payload + includeTotal.
 *   Provide cache.key to override this behavior when appropriate.
 *
 * Response shape notes:
 * - By default, the hook stores response.content if present; otherwise it stores the full response object.
 *   If you want the entire raw response consistently, pass includeTotal: true.
 *
 * Common usage patterns:
 * 1) Basic GET, cache-first with TTL
 *    const { data, loading } = useApiService<T>({
 *      url: "/items",
 *      method: "GET",
 *      apiName: "core",
 *      cache: { enabled: true, strategy: "cache-first", ttlMs: 5 * 60_000 },
 *    });
 *
 * 2) Stale-while-revalidate
 *    const { data, revalidate } = useApiService<T>({
 *      url: "/items",
 *      method: "GET",
 *      apiName: "core",
 *      cache: { enabled: true, strategy: "stale-while-revalidate", ttlMs: 10_000 },
 *    });
 *    // later, manually refresh
 *    await revalidate();
 *
 * 3) Custom cache key (e.g., ignore payload differences)
 *    const { data } = useApiService<T>({
 *      url: "/report",
 *      method: "POST",
 *      payload: { filter: "A" },
 *      cache: { enabled: true, key: "report:all" },
 *    });
 *
 * 4) Per-call overrides
 *    await fetchData({ revalidate: true });            // force refresh now (network-only for this call)
 *    await fetchData({ noCache: true });               // bypass cache read/write once
 *    await fetchData({ cacheStrategy: "stale-while-revalidate" }); // override strategy per call
 *
 * 5) Invalidate explicitly (e.g., after a mutation)
 *    const { invalidate } = useApiService({ url: "/items", method: "GET", cache: { enabled: true } });
 *    // after successful POST/PUT/DELETE affecting this list:
 *    invalidate(); // or invalidate("your-custom-key");
 */
import { useEffect, useState, useRef, useCallback } from "react";
import { apiDelete, apiGet, apiPost, apiPut } from "../service/api-service";

// ---- Simple in-memory cache (module-level) ----
// Survives across hook instances within the same JS context

type CacheEntry<T> = {
  data: T;
  timestamp: number;
  ttl?: number; // ms
};

const apiCache = new Map<string, CacheEntry<any>>();
const inflightRequests = new Map<string, Promise<any>>();

// Stable-ish key serialization for params/payload
function stableStringify(value: unknown): string {
  if (value === null || typeof value !== "object") return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`;
  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj).sort();
  return `{${keys.map(k => `${JSON.stringify(k)}:${stableStringify(obj[k])}`).join(",")}}`;
}

function makeCacheKey(options: {
  method: string;
  apiName: string;
  url: string;
  params?: Record<string, unknown>;
  payload?: any;
  includeTotal?: boolean;
  override?: string;
}) {
  if (options.override) return options.override;
  // include method+api+url+params+payload+includeTotal so we don’t mix different shapes
  return [
    options.method,
    options.apiName,
    options.url,
    stableStringify(options.params || {}),
    stableStringify(options.payload),
    options.includeTotal ? "withTotal" : "contentOnly",
  ].join("::");
}

function getCached<T>(key: string): T | null {
  const entry = apiCache.get(key) as CacheEntry<T> | undefined;
  if (!entry) return null;
  if (entry.ttl && Date.now() - entry.timestamp > entry.ttl) {
    apiCache.delete(key);
    return null;
  }
  return entry.data;
}

function setCached<T>(key: string, data: T, ttl?: number) {
  apiCache.set(key, { data, timestamp: Date.now(), ttl });
}

function clearCacheKey(key: string) {
  apiCache.delete(key);
}

/**
 * Invalidate a specific cache key from anywhere without needing a hook instance.
 * @param key - The cache key to invalidate
 */
export function invalidateCacheKey(key: string) {
  clearCacheKey(key);
}

/**
 * Get the cache key for a specific request configuration.
 * Useful when you need to invalidate a cache key from a different hook/component.
 */
export function getCacheKey(options: {
  method: string;
  apiName: string;
  url: string;
  params?: Record<string, unknown>;
  payload?: any;
  includeTotal?: boolean;
  key?: string;
}): string {
  return makeCacheKey({
    method: options.method,
    apiName: options.apiName,
    url: options.url,
    params: options.params,
    payload: options.payload,
    includeTotal: options.includeTotal,
    override: options.key,
  });
}

export type CacheStrategy =
  | "network-only" // always fetch
  | "cache-first" // if cache hit, return it and don’t fetch
  | "stale-while-revalidate"; // return cached immediately, then refresh in background

function useApiService<T>({
  url,
  method,
  apiName = "open",
  payload = null,
  params = {},
  onSuccess,
  onError,
  fetchOnMount = true,
  dependencies = [],
  includeTotal = false,
  // --- cache options ---
  cache = {
    enabled: false,
    strategy: "cache-first" as CacheStrategy,
    ttlMs: 60 * 60_000, // default 1 hour TTL
    key: undefined as string | undefined,
  },
}: {
  url: string;
  method: "GET" | "POST" | "PUT" | "DELETE";
  apiName?: "open" | "core";
  payload?: any;
  params?: Record<string, unknown>;
  onSuccess?: (data: T) => void;
  onError?: (error: any) => void;
  fetchOnMount?: boolean;
  dependencies?: any[];
  includeTotal?: boolean;
  cache?: {
    enabled?: boolean; // optional caching
    strategy?: CacheStrategy; // cache-first | stale-while-revalidate | network-only
    ttlMs?: number; // optional TTL in ms
    key?: string; // optional custom cache key (e.g., if you want to ignore payload)
  };
}): {
  data: T | null;
  loading: boolean;
  error: any;
  fetchData: (options?: {
    fetchParams?: Record<string, unknown>;
    url?: string;
    payload?: any;
    revalidate?: boolean; // force refresh even if cached
    cacheStrategy?: CacheStrategy; // override per-call
    noCache?: boolean; // skip reading and writing cache for this call
  }) => Promise<T | null>;
  revalidate: (options?: {
    url?: string;
    payload?: any;
    fetchParams?: Record<string, unknown>;
  }) => Promise<T | null>;
  invalidate: (keyOverride?: string) => void;
} {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(fetchOnMount);
  const [error, setError] = useState<any>(null);
  const isMounted = useRef(true);

  const baseKey = cache.enabled
    ? makeCacheKey({
        method,
        apiName,
        url,
        params,
        payload,
        includeTotal,
        override: cache.key,
      })
    : "";

  const doNetworkCall = useCallback(
    async (
      fetchUrl: string,
      fetchPayload: any,
      fetchParams: Record<string, unknown> | undefined,
      cacheKey?: string,
      noCache?: boolean
    ) => {
      let response;
      switch (method) {
        case "GET":
          response = await apiGet(apiName, fetchUrl, {
            queryParams: { ...params, ...fetchParams },
          });
          break;
        case "POST":
          response = await apiPost(apiName, fetchUrl, fetchPayload);

          break;
        case "PUT":
          response = await apiPut(apiName, fetchUrl, fetchPayload);
          break;
        case "DELETE":
          response = await apiDelete(apiName, fetchUrl);
          break;
        default:
          throw new Error(`Unsupported method: ${method}`);
      }

      const result = (includeTotal ? response : (response?.content ?? response)) as T;

      if (!noCache && cache.enabled && cacheKey) {
        setCached(cacheKey, result, cache.ttlMs);
      }
      return result;
    },
    [apiName, includeTotal, method, params, cache.enabled, cache.ttlMs]
  );

  const fetchData = useCallback(
    async (options?: {
      fetchParams?: Record<string, unknown>;
      url?: string;
      payload?: any;
      revalidate?: boolean;
      cacheStrategy?: CacheStrategy;
      noCache?: boolean;
    }): Promise<T | null> => {
      setLoading(true);
      setError(null);

      const fetchUrl = options?.url || url;
      const fetchPayload = options?.payload ?? payload;
      const fetchParams = options?.fetchParams;

      const key =
        cache.enabled && !options?.noCache
          ? makeCacheKey({
              method,
              apiName,
              url: fetchUrl,
              params: { ...params, ...fetchParams },
              payload: fetchPayload,
              includeTotal,
              override: cache.key,
            })
          : undefined;

      const effectiveStrategy: CacheStrategy = options?.revalidate
        ? "network-only"
        : options?.cacheStrategy || cache.strategy || "cache-first";

      // 1) Try cache if enabled and allowed
      if (cache.enabled && key && effectiveStrategy !== "network-only") {
        const cached = getCached<T>(key);
        if (cached != null) {
          if (isMounted.current) setData(cached);
          // Always notify success when serving from cache, regardless of strategy
          onSuccess?.(cached);

          if (effectiveStrategy === "cache-first") {
            setLoading(false);
            return cached;
          }
          // stale-while-revalidate: return cached now, then refresh in bg
          // Kick off background refresh (deduped)
          if (!inflightRequests.has(key)) {
            const p = doNetworkCall(fetchUrl, fetchPayload, fetchParams, key, false)
              .then(fresh => {
                if (isMounted.current) {
                  setData(fresh);
                  onSuccess?.(fresh);
                }
                return fresh;
              })
              .catch(e => {
                if (isMounted.current) {
                  setError(e);
                  onError?.(e);
                }
              })
              .finally(() => {
                inflightRequests.delete(key);
              });
            inflightRequests.set(key, p);
          }
          setLoading(false);
          return cached;
        }
      }

      // 2) No suitable cache (or network-only/revalidate). Fetch from network, with in-flight dedupe.
      try {
        // If we have a key, dedupe concurrent requests
        if (key) {
          if (inflightRequests.has(key)) {
            const result = await inflightRequests.get(key)!;
            if (isMounted.current && result != null) {
              setData(result);
              onSuccess?.(result);
            }
            return result ?? null;
          } else {
            const p = doNetworkCall(fetchUrl, fetchPayload, fetchParams, key, !!options?.noCache)
              .then(result => {
                if (isMounted.current) {
                  setData(result);
                  onSuccess?.(result);
                }
                return result;
              })
              .catch(e => {
                if (isMounted.current) {
                  setError(e);
                  onError?.(e);
                }
                throw e;
              })
              .finally(() => {
                inflightRequests.delete(key);
              });
            inflightRequests.set(key, p);
            const out = await p;
            return out ?? null;
          }
        } else {
          // No cache key => just do the network call
          const result = await doNetworkCall(fetchUrl, fetchPayload, fetchParams, undefined, true);
          if (isMounted.current) {
            setData(result);
            onSuccess?.(result);
          }
          return result ?? null;
        }
      } finally {
        if (isMounted.current) setLoading(false);
      }
    },
    [
      url,
      payload,
      params,
      includeTotal,
      cache.enabled,
      cache.key,
      cache.strategy,
      doNetworkCall,
      method,
      apiName,
      onSuccess,
      onError,
    ]
  );

  const revalidate = useCallback(
    async (options?: { url?: string; payload?: any; fetchParams?: Record<string, unknown> }) => {
      return fetchData({ ...options, revalidate: true });
    },
    [fetchData]
  );

  const invalidate = useCallback(
    (keyOverride?: string) => {
      if (!cache.enabled) return;
      const key = keyOverride || baseKey;
      if (key) clearCacheKey(key);
    },
    [cache.enabled, baseKey]
  );

  useEffect(() => {
    isMounted.current = true;
    if (fetchOnMount) {
      // If cache-first and we already have a cached item, avoid a flash of loading
      if (cache.enabled && baseKey) {
        const cached = getCached<T>(baseKey);
        if (cached != null) {
          setData(cached);
          setLoading(false);
          // Notify success when serving from cache on mount
          onSuccess?.(cached);
          if (cache.strategy === "stale-while-revalidate") {
            // trigger background refresh
            void revalidate();
          }
        } else {
          void fetchData();
        }
      } else {
        void fetchData();
      }
    }
    return () => {
      isMounted.current = false;
    };
    // Important: baseKey already captures url/method/apiName/payload/params/includeTotal + custom key
  }, [fetchOnMount, baseKey, cache.enabled, cache.strategy]);

  return { data, loading, error, fetchData, revalidate, invalidate };
}

export default useApiService;
